/**
 * Shared utility functions for scene loading plugins.
 *
 * This module contains pure scene-document loading utilities with no Trace-specific dependencies.
 * It can be shipped independently with GltfSceneLoaderPlugin.
 *
 * The three-pass loading pattern:
 * 1. Create TransformNodes for each parsed node
 * 2. Establish parent-child hierarchy
 * 3. Load assets and build result
 */

import type { ISceneLoaderAsyncResult, ISceneLoaderProgressEvent } from "@babylonjs/core/Loading/sceneLoader";
import type { Scene } from "@babylonjs/core/scene";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import type { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import type { AssetContainer } from "@babylonjs/core/assetContainer";
import type { AnimationGroup } from "@babylonjs/core/Animations/animationGroup";
import type { Skeleton } from "@babylonjs/core/Bones/skeleton";
import { Vector3, Quaternion } from "@babylonjs/core/Maths/math.vector";
import { Logger } from '../../utils/Logger';
import type { ForeignDocumentData, ForeignNodeData } from '../formats/gltf21/foreign';
import type { AnimationBindingData } from '../../scenedata/sceneobjectdata';

// =============================================================================
// Base Types (glXF-compatible, no Trace dependencies)
// =============================================================================

/**
 * Minimal interface for parsed nodes.
 * Both the .trace parsed-node shape and ParsedDocumentNode satisfy it.
 */
export interface ParsedNodeBase {
    name: string;
    assetUri?: string;
    transform?: {
        translation?: [number, number, number];
        rotation?: [number, number, number, number];
        scale?: [number, number, number];
    };
    parentName?: string;
    /**
     * The parent itself, when the format names it exactly (a glTF node's
     * `children` index). Wins over `parentName`, which cannot tell two nodes
     * with the same name apart.
     */
    parentNode?: ParsedNodeBase;
}

/**
 * Base loaded node type for glXF scenes. Includes optional Trace-extras
 * passthrough fields (`behaviors`, `active`) so a standalone `.glxf` carrying
 * `extras.trace` round-trips through `loadFromPluginResult` the same way a
 * `.tracex` archive does.
 */
export interface GLXFLoadedNode {
    /** The Babylon TransformNode for this scene node */
    transform: TransformNode;
    /** Node name */
    name: string;
    /**
     * glTF 2.1 node identity (`node.uid`, or the `name` 2.1's fallback rule
     * elected). Carried so a load → save round trip preserves it byte for byte
     * (issue #837). Absent for legacy formats, which have no such concept.
     */
    uid?: string;
    /** Root mesh if this node has a loaded asset */
    mesh?: AbstractMesh;
    /** Animation groups from the loaded asset */
    animationGroups?: AnimationGroup[];
    /** Skeletons from the loaded asset */
    skeletons?: Skeleton[];
    /** Original asset URI (for reference/debugging) */
    assetUri?: string;
    /**
     * Original `src` reference from the source document, preserved across the
     * load → edit → re-export round-trip. For `.glxf` this is the verbatim
     * `assets[i].uri` (typically an absolute URL). Consumed by TraceScene to
     * populate `SceneObject.src` so re-publishing emits the same reference.
     */
    src?: string;
    /**
     * Absolute URL the (relative) `src` resolves to, when the document was
     * loaded from one. Registered with the ResourceManager so a behavior that
     * owns the asset can fetch it from the folder while `src` stays relative.
     */
    srcUrl?: string;
    /**
     * `files[k].name` — the file's ORIGINAL human name (spec §2.1),
     * informational. Storage names blobs by hash; this is the name a person
     * would recognise, and nothing ever resolves by it.
     */
    srcName?: string;
    /**
     * True when `src` references another scene — an archive or a glTF
     * document — so the nested-scene path owns the load, not this plugin.
     */
    sceneRef?: boolean;
    /**
     * Whether the node should be active/enabled (from `extras.trace.active`).
     * A STRING is a property binding (`"{visible}"`) — authored data that the
     * runtime resolves and that must round-trip (SCENE_ROOT_SPEC §2.1).
     */
    active?: boolean | string;
    /** Trace behaviors to attach (from `extras.trace.behaviors`). */
    behaviors?: unknown[];
    /**
     * True when the LOADER already instantiated this node's file into the
     * scene, so the runtime must not add a second owner for the same bytes.
     *
     * `SceneObject.src` implies an asset-owning behavior ("src as template",
     * `SceneCreation.ensurePrimitiveBehaviorData`), and that behavior loads
     * the file itself. For a node the plugin inline-loaded, that is the same
     * file twice: two containers, two copies of the geometry at the same
     * absolute position, two draw calls. Exactly one of the two owns it —
     * this flag says which, per node.
     */
    assetInstantiated?: boolean;
    /** Error message if asset loading failed */
    loadError?: string;
    /** glTF extensions used by the loaded asset (from extensionsUsed in the glTF JSON) */
    extensionsUsed?: string[];

    /**
     * What the document's node carried that Trace did not author — `extras`
     * beside `trace`, and `extensions`. Nothing reads it; it rides through to
     * `SceneObject` so the next save writes it back (issue #834, spec §3.6).
     */
    foreign?: ForeignNodeData;

    /**
     * `TRACE_animation_binding` on the document node, as the session holds it
     * (sources by file reference). The writer owns it: it is never foreign.
     */
    animationBinding?: AnimationBindingData;

    /**
     * Folder-relative binding sources → the absolute URL each resolves to,
     * registered like a node `src`'s (`ResourceManager.registerAssetUrl`).
     */
    animationSourceUrls?: Array<[string, string]>;
}

/**
 * Base result type for glXF scene loading.
 * Contains only standard glTF/glXF concepts.
 */
export interface GLXFLoadedResult<TNode extends GLXFLoadedNode = GLXFLoadedNode> {
    /** All loaded nodes */
    nodes: TNode[];
    /** Root nodes (nodes without parents) */
    rootNodes: TNode[];
    /** Map of node name to loaded node for quick lookup */
    nodeMap: Map<string, TNode>;
    /**
     * The document root — the one node of `scenes[0].nodes[]` (SCENE_ROOT_SPEC
     * D8), after the read-boundary migration (§3). Absent only for a foreign
     * multi-root document that was placed rather than opened as the scene.
     */
    documentRoot?: TNode;
    /** Script `@type` → asset path, from the document's `extras.trace.scripts`. */
    scripts?: Record<string, string>;
    /**
     * The document's foreign half — extras beside `trace`, extensions,
     * `extensionsUsed`/`Required`, `asset.extras` (issue #834). Held by
     * `TraceScene` until the next save merges it back in.
     */
    foreignDocument?: ForeignDocumentData;
}

/**
 * Options for the loadNodesWithResult utility function.
 */
export interface SceneLoadingOptions<
    TNode extends ParsedNodeBase,
    TLoadedNode extends GLXFLoadedNode = GLXFLoadedNode
> {
    /** Parsed nodes to load */
    nodes: TNode[];
    /** Target Babylon scene */
    scene: Scene;
    /** Progress callback */
    onProgress?: (event: ISceneLoaderProgressEvent) => void;
    /** Function to load a single asset (allows different transform semantics per plugin) */
    loadAsset: (assetUri: string, parent: TransformNode) => Promise<ISceneLoaderAsyncResult & { extensionsUsed?: string[] }>;
    /** Optional hook to extract additional metadata from parsed node to loaded node */
    extractMetadata?: (parsedNode: TNode, loadedNode: TLoadedNode) => void;
    /** Factory to create loaded nodes (defaults to creating GLXFLoadedNode) */
    createLoadedNode?: (transform: TransformNode, name: string, assetUri?: string) => TLoadedNode;
}

/**
 * Options for the loadNodesIntoContainer utility function.
 */
export interface ContainerLoadingOptions<TNode extends ParsedNodeBase> {
    /** Parsed nodes to load */
    nodes: TNode[];
    /** Target asset container */
    container: AssetContainer;
    /** Target Babylon scene */
    scene: Scene;
    /** Progress callback */
    onProgress?: (event: ISceneLoaderProgressEvent) => void;
    /** Function to load a single asset */
    loadAsset: (assetUri: string, parent: TransformNode) => Promise<ISceneLoaderAsyncResult & { extensionsUsed?: string[] }>;
}

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Apply transform data to a TransformNode.
 */
export function applyTransform(
    node: TransformNode,
    transform?: ParsedNodeBase['transform']
): void {
    if (!transform) return;

    if (transform.translation) {
        node.position = new Vector3(
            transform.translation[0],
            transform.translation[1],
            transform.translation[2]
        );
    }

    if (transform.rotation) {
        node.rotationQuaternion = new Quaternion(
            transform.rotation[0],
            transform.rotation[1],
            transform.rotation[2],
            transform.rotation[3]
        );
    }

    if (transform.scale) {
        node.scaling = new Vector3(
            transform.scale[0],
            transform.scale[1],
            transform.scale[2]
        );
    }
}

/**
 * Compute the full path of a TransformNode by walking up the parent chain.
 * Returns a path like "/Root/Child/GrandChild".
 *
 * @param transform - The TransformNode to compute the path for
 * @returns The full path from root to this node
 */
export function computeNodePath(transform: TransformNode): string {
    const parts: string[] = [];
    let current: TransformNode | null = transform;
    while (current) {
        parts.unshift(current.name);
        current = current.parent as TransformNode | null;
    }
    return '/' + parts.join('/');
}

/**
 * Handle Babylon's __root__ container node.
 * If the root has a single child, promotes it and disposes the root.
 * Otherwise, parents the root directly.
 */
export function processRootNode(rootMesh: AbstractMesh, parent: TransformNode): TransformNode {
    const children = rootMesh.getChildren<TransformNode>();

    if (children.length === 1) {
        const modelContent = children[0];
        modelContent.parent = parent;
        rootMesh.dispose();
        return modelContent;
    } else {
        rootMesh.parent = parent;
        return rootMesh;
    }
}

/**
 * Default factory for creating GLXFLoadedNode instances.
 */
function createDefaultLoadedNode(
    transform: TransformNode,
    name: string,
    assetUri?: string
): GLXFLoadedNode {
    return { transform, name, assetUri };
}

/**
 * Result of building the transform hierarchy.
 * Contains maps for looking up transforms by node object reference.
 */
export interface TransformHierarchyResult<TNode extends ParsedNodeBase> {
    /** Map from parsed node to its TransformNode (primary lookup - no duplicates) */
    nodeTransforms: Map<TNode, TransformNode>;
    /** Map from path to TransformNode (for path-based lookups) */
    pathMap: Map<string, TransformNode>;
    /** Map from parsed node to its file-local path */
    nodePaths: Map<TNode, string>;
}

/**
 * Build transform hierarchy from parsed nodes (passes 1 & 2).
 *
 * This is the shared implementation for creating TransformNodes and establishing
 * parent-child relationships. Both `loadNodesWithResult` and `loadNodesIntoContainer`
 * use this internally.
 *
 * @param nodes - Parsed nodes to create transforms for
 * @param scene - Target Babylon scene
 * @param onTransformCreated - Optional callback after each transform is created (e.g., to add to container)
 * @returns Maps for looking up transforms
 */
export function buildTransformHierarchy<TNode extends ParsedNodeBase>(
    nodes: TNode[],
    scene: Scene,
    onTransformCreated?: (node: TNode, transform: TransformNode) => void
): TransformHierarchyResult<TNode> {
    // Pass 1: Create transform nodes
    const nodeTransforms = new Map<TNode, TransformNode>();
    const nameMap = new Map<string, TransformNode>(); // For legacy parent lookups

    for (const node of nodes) {
        const transform = new TransformNode(node.name, scene);
        applyTransform(transform, node.transform);
        nodeTransforms.set(node, transform);
        nameMap.set(node.name, transform);
        onTransformCreated?.(node, transform);
    }

    // Pass 2: Establish parent hierarchy and build path map
    const pathMap = new Map<string, TransformNode>();
    const nodePaths = new Map<TNode, string>();
    for (const node of nodes) {
        const transform = nodeTransforms.get(node)!;

        if (node.parentName) {
            // Strip leading '/' if present (legacy format used "/Parent", new format uses "Parent")
            const normalizedParent = node.parentName.startsWith('/')
                ? node.parentName.slice(1)
                : node.parentName;
            // The exact parent first, then path lookup, then bare name fallback for flat scenes
            const parent = (node.parentNode ? nodeTransforms.get(node.parentNode as TNode) : undefined)
                ?? pathMap.get(normalizedParent)
                ?? pathMap.get(node.parentName)
                ?? nameMap.get(normalizedParent)
                ?? nameMap.get(node.parentName);
            if (parent) {
                transform.parent = parent;
            }
        }

        // Build path using declared parentName (file-local resolution).
        // We can't use computeNodePath() here because it walks the live Babylon.js
        // parent chain, which diverges from the declared paths once nodes are nested.
        // Strip leading '/' from parentName for consistent path building.
        const normalizedParent = node.parentName?.startsWith('/')
            ? node.parentName.slice(1)
            : node.parentName;
        const parentPath = (node.parentNode ? nodePaths.get(node.parentNode as TNode) : undefined) ?? normalizedParent;
        const localPath = parentPath ? `${parentPath}/${node.name}` : node.name;
        pathMap.set(localPath, transform);
        nodePaths.set(node, localPath);
    }

    return { nodeTransforms, pathMap, nodePaths };
}

/**
 * Load all nodes using the three-pass pattern and return GLXFLoadedResult.
 *
 * Pass 1: Create TransformNodes for each parsed node
 * Pass 2: Establish parent-child hierarchy
 * Pass 3: Load assets and build loaded node array
 */
export async function loadNodesWithResult<
    TNode extends ParsedNodeBase,
    TLoadedNode extends GLXFLoadedNode = GLXFLoadedNode
>(
    options: SceneLoadingOptions<TNode, TLoadedNode>
): Promise<GLXFLoadedResult<TLoadedNode>> {
    const {
        nodes,
        scene,
        onProgress,
        loadAsset,
        extractMetadata,
        createLoadedNode = createDefaultLoadedNode as (t: TransformNode, n: string, u?: string) => TLoadedNode,
    } = options;
    const loadedNodes: TLoadedNode[] = [];
    const nodeMap = new Map<string, TLoadedNode>();

    // Passes 1 & 2: Build transform hierarchy
    const { nodeTransforms, nodePaths } = buildTransformHierarchy(nodes, scene);

    // Pass 3: Load assets concurrently (matching the .trace path, which loads
    // all Model behaviors via Promise.all) and build the loaded node array.
    // Node order in loadedNodes stays deterministic; progress counts as each
    // load settles. completedCount++ is safe since JS is single-threaded.
    const totalAssets = nodes.filter(n => n.assetUri).length;
    let loadedCount = 0;
    const assetLoads: Promise<void>[] = [];

    for (const node of nodes) {
        const transform = nodeTransforms.get(node)!;
        const path = nodePaths.get(node)!;
        const loadedNode = createLoadedNode(transform, node.name, node.assetUri);

        // Extract additional metadata using the provided hook
        if (extractMetadata) {
            extractMetadata(node, loadedNode);
        }

        if (node.assetUri) {
            const assetUri = node.assetUri;
            assetLoads.push(
                loadAsset(assetUri, transform)
                    .then(result => {
                        // Store mesh and animation info
                        if (result.meshes.length > 0) {
                            loadedNode.mesh = result.meshes[0];
                        }
                        if (result.animationGroups.length > 0) {
                            loadedNode.animationGroups = result.animationGroups;
                        }
                        if (result.skeletons.length > 0) {
                            loadedNode.skeletons = result.skeletons;
                        }
                        if (result.extensionsUsed) {
                            loadedNode.extensionsUsed = result.extensionsUsed;
                        }
                    })
                    .catch((error: unknown) => {
                        // Log warning and continue loading other nodes
                        Logger.warn(
                            `Failed to load asset "${assetUri}" for node "${node.name}":`,
                            error instanceof Error ? error.message : String(error)
                        );
                        loadedNode.loadError = error instanceof Error ? error.message : String(error);
                    })
                    .then(() => {
                        loadedCount++;
                        onProgress?.({
                            lengthComputable: true,
                            loaded: loadedCount,
                            total: totalAssets,
                        });
                    })
            );
        }

        loadedNodes.push(loadedNode);
        nodeMap.set(path, loadedNode);
    }

    await Promise.all(assetLoads);

    // Find root nodes (no parent)
    const rootNodes = loadedNodes.filter(n => !n.transform.parent);

    return {
        nodes: loadedNodes,
        rootNodes,
        nodeMap,
    };
}

/**
 * Load nodes into an asset container using the three-pass pattern.
 */
export async function loadNodesIntoContainer<TNode extends ParsedNodeBase>(
    options: ContainerLoadingOptions<TNode>
): Promise<void> {
    const { nodes, container, scene, onProgress, loadAsset } = options;

    // Passes 1 & 2: Build transform hierarchy, adding each transform to container
    const { nodeTransforms } = buildTransformHierarchy(nodes, scene, (_node, transform) => {
        container.transformNodes.push(transform);
    });

    // Pass 3: Load assets concurrently (matching the .trace path). Results
    // are collected per node and pushed into the container in node order so
    // container contents stay deterministic regardless of settle order.
    const assetNodes = nodes.filter(n => n.assetUri);
    const totalAssets = assetNodes.length;
    let loadedCount = 0;

    const results = await Promise.all(
        assetNodes.map(node =>
            loadAsset(node.assetUri!, nodeTransforms.get(node)!)
                .catch((error: unknown) => {
                    // Log warning and continue loading other nodes
                    Logger.warn(
                        `Failed to load asset "${node.assetUri}" for node "${node.name}":`,
                        error instanceof Error ? error.message : String(error)
                    );
                    return null;
                })
                .then(result => {
                    loadedCount++;
                    onProgress?.({
                        lengthComputable: true,
                        loaded: loadedCount,
                        total: totalAssets,
                    });
                    return result;
                })
        )
    );

    for (const result of results) {
        if (!result) continue;
        container.meshes.push(...result.meshes);
        container.animationGroups.push(...result.animationGroups);
        container.skeletons.push(...result.skeletons);
    }
}
